"use client";


import { useTranslation } from "react-i18next";
import { History, Loader2 } from "lucide-react";
import { useAppointmentModal } from "@/contexts/AppointmentModalContext";
import { useRecordRevisions } from "@/hooks/modal/useRecordRevisions";
import type { RevisionItem } from "@/lib/types/revisions";

/**
 * Format ISO timestamp to HH:mm format
 */
function formatTime(changedAt: string): string {
    try {
        const date = new Date(changedAt);
        return date.toLocaleTimeString("ru-RU", {
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return "";
    }
}

/**
 * Format ISO timestamp to short date (DD.MM)
 */
function formatShortDate(changedAt: string): string {
    try {
        const date = new Date(changedAt);
        return date.toLocaleDateString("ru-RU", {
            day: "2-digit",
            month: "2-digit",
        });
    } catch {
        return "";
    }
}

interface RevisionCardProps {
    revision: RevisionItem;
}

function RevisionCard({ revision }: RevisionCardProps) {
    const { t } = useTranslation();

    return (
        <div className="bg-white rounded-lg border shadow-sm p-3">
            {/* Header: user + timestamp */}
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                    <span className="font-medium text-sm text-gray-900">
                        {revision.changed_by.name}
                    </span>
                    <span className="text-xs text-gray-400">
                        ({revision.changed_by.type === "user"
                            ? t("changes.user_type.admin", "админ")
                            : t("changes.user_type.client", "клиент")})
                    </span>
                </div>
                <div className="text-xs text-gray-400">
                    {formatShortDate(revision.changed_at)} {formatTime(revision.changed_at)}
                </div>
            </div>
            {/* Labels */}
            <ul className="text-sm text-gray-600 space-y-1">
                {revision.labels.map((label, idx) => (
                    <li key={idx} className="flex items-start">
                        <span className="text-cyan-500 mr-2 mt-0.5">•</span>
                        <span className="break-words">{label}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

export function ChangesSectionMobile() {
    const { t } = useTranslation();
    const { appointment } = useAppointmentModal();

    const recordId = appointment?.id ? Number(appointment.id) : undefined;
    const { data: revisions = [], isLoading, error } = useRecordRevisions(recordId);

    if (isLoading) {
        return (
            <div className="py-8 flex items-center justify-center text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                <span className="text-sm">{t("changes.loading", "Loading...")}</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="py-8 text-center text-sm text-red-500">
                {t("changes.error", "Error loading change history")}
            </div>
        );
    }

    if (revisions.length === 0) {
        return (
            <div className="py-8 flex flex-col items-center justify-center text-gray-400">
                <History className="w-10 h-10 mb-2 stroke-[1.5]" />
                <span className="text-sm">{t("changes.empty", "There are no changes yet")}</span>
            </div>
        );
    }

    return (
        <div className="space-y-3">
            {revisions.map((revision) => (
                <RevisionCard key={revision.id} revision={revision} />
            ))}
        </div>
    );
}
